import { useContext, useEffect, useMemo, useState, useTransition } from "react";

import { DropdownSourceContext } from "@ryde/redux";
import {
  useFormats,
  type IAvailability,
  type IFormAttributes,
} from "@ryde/shared";

import type { IExpandedAvailability } from "./availability-cell.models";
import {
  calculateMoreItemsCount,
  expandAvailabilityItem,
  generateDisplayText,
} from "./availability-cell.utils";

export const useAvailabilityCell = (
  value: IAvailability[],
  formAttributes?: IFormAttributes | null,
) => {
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const { timeFormat } = useFormats();
  const [displayValues, setDisplayValues] = useState<IExpandedAvailability[]>(
    [],
  );
  const [, startTransition] = useTransition();

  useEffect(() => {
    const fetchAndExpand = async (): Promise<void> => {
      if (!value?.length) {
        return;
      }

      const [dayTypeItems, dayGroupItems] = await Promise.all([
        getDropdownItems?.("dayType"),
        getDropdownItems?.("dayGroup"),
      ]);

      const expanded = value.map(item =>
        expandAvailabilityItem({ item, dayTypeItems, dayGroupItems }),
      );

      setDisplayValues(expanded);
    };

    startTransition(() => {
      fetchAndExpand();
    });
  }, [value, formAttributes?.dropItemsSource, getDropdownItems]);

  const displayText = useMemo(
    () => generateDisplayText(displayValues, timeFormat),
    [displayValues, timeFormat],
  );

  const moreItemsCount = useMemo(
    () => calculateMoreItemsCount(displayValues.length),
    [displayValues],
  );

  return { displayValues, displayText, moreItemsCount };
};
